import { useCallback, useEffect, useState } from 'react';

import { routes } from '../../../assets/constants';
import { ApiMethod } from '../../../common/models/api-types.type';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';

/**
 * Pinterest as a Studio inspiration source — mirrors pms-api-v2's
 * `services/pms/pinterest` controller (pms-social-scheduler module).
 */

export type PinterestPinKind = 'image' | 'video';

export interface PinterestPin {
    id: string;
    title?: string;
    link?: string;
    kind: PinterestPinKind;
    /** The largest still: the picture itself, or a video's cover. */
    imageUrl: string;
    /** Video pins: an mp4 when Pinterest has one, otherwise an HLS playlist the import swaps for one. */
    videoUrl?: string;
    width?: number;
    height?: number;
    pinUrl?: string;
}

export interface PinterestBoard {
    id: string;
    name: string;
    description?: string;
    pinCount?: number;
    coverUrl?: string;
    privacy?: string;
}

export interface PinterestPage<T> {
    items: T[];
    bookmark?: string;
}

export interface PinterestStatus {
    /** False when the server has no Pinterest app yet: shown as "coming soon", never as an error. */
    configured: boolean;
    connected: boolean;
    account?: {
        uuid: string;
        username: string;
        name: string;
        avatar?: string;
        connectedAt: string;
        /** 'expired' keeps the account visible so the vendor knows which one to reconnect. */
        status: 'active' | 'expired' | string;
    };
}

export interface PinterestImport {
    /** Our storage: CORS-safe, so the browser can read it as a Blob. */
    url: string;
    kind: PinterestPinKind;
    width?: number;
    height?: number;
    contentType: string;
    bytes: number;
    durationSec?: number;
}

/** 409 codes the API uses for "not connected" and "connection expired". */
export const isPinterestConnectionError = (err: any): boolean =>
    err?.body?.code === 'PINTEREST_NOT_CONNECTED' || err?.body?.code === 'PINTEREST_EXPIRED';

export const usePinterestApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const call = useCallback(
        async <T>(method: ApiMethod, path: string, body?: any): Promise<T> =>
            ((await sendAuthGuardedRequest(method, path, body)) as any) as T,
        [sendAuthGuardedRequest],
    );

    const withPage = (path: string, bookmark?: string) =>
        bookmark ? `${path}?bookmark=${encodeURIComponent(bookmark)}` : path;

    return {
        getStatus: () => call<PinterestStatus>(ApiMethod.GET, routes.pinterest.status),
        /** `returnTo` is the in-app path Pinterest should bring the vendor back to. */
        startOAuth: (returnTo?: string) =>
            call<{ authUrl: string }>(
                ApiMethod.GET,
                returnTo ? `${routes.pinterest.oauthStart}?returnTo=${encodeURIComponent(returnTo)}` : routes.pinterest.oauthStart,
            ),
        disconnect: () => call<{ success: boolean }>(ApiMethod.DELETE, routes.pinterest.disconnect),
        listBoards: (bookmark?: string) => call<PinterestPage<PinterestBoard>>(ApiMethod.GET, withPage(routes.pinterest.boards, bookmark)),
        listBoardPins: (boardId: string, bookmark?: string) =>
            call<PinterestPage<PinterestPin>>(ApiMethod.GET, withPage(routes.pinterest.boardPins(boardId), bookmark)),
        listMyPins: (bookmark?: string) => call<PinterestPage<PinterestPin>>(ApiMethod.GET, withPage(routes.pinterest.myPins, bookmark)),
        /** Any public pin link (pinterest.com/pin/…, pin.it/…) or a bare pin id — no account needed. */
        resolve: (url: string) => call<PinterestPin>(ApiMethod.POST, routes.pinterest.resolve, { url }),
        importMedia: (body: { imageUrl?: string; videoUrl?: string; pinId?: string }) =>
            call<PinterestImport>(ApiMethod.POST, routes.pinterest.import, body),
    };
};

/** The path to come back to after Pinterest's consent screen: wherever the vendor is now. */
const currentAppPath = () => `${window.location.pathname}${window.location.search}`;

/**
 * Is Pinterest set up on this server, and is this vendor connected — plus the
 * two actions that change the answer. `connect` leaves the page (Pinterest's
 * consent screen) and comes back to where it was called from.
 */
export const usePinterestStatus = () => {
    const api = usePinterestApi();
    const [status, setStatus] = useState<PinterestStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [redirecting, setRedirecting] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setStatus(await api.getStatus());
        } catch (err: any) {
            setError(err?.message || 'error');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const connect = useCallback(async (returnTo?: string) => {
        setRedirecting(true);
        try {
            const { authUrl } = await api.startOAuth(returnTo ?? currentAppPath());
            window.location.href = authUrl;
        } catch (err) {
            setRedirecting(false);
            throw err;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const disconnect = useCallback(async () => {
        await api.disconnect();
        await refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh]);

    return {
        status,
        loading,
        error,
        redirecting,
        configured: Boolean(status?.configured),
        connected: Boolean(status?.connected),
        expired: status?.account?.status === 'expired',
        refresh,
        connect,
        disconnect,
    };
};

const EXTENSIONS: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/avif': 'avif',
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/webm': 'webm',
};

/**
 * Bring a pin in as a real File, exactly like one dropped on the page.
 *
 * The server copies the media into our storage first: pinimg does not let a
 * browser read its files as Blobs, ours does. A video pin listed without a
 * playable address is resolved again first — its page usually has one.
 */
export async function importPinAsFile(
    api: ReturnType<typeof usePinterestApi>,
    pin: PinterestPin,
): Promise<{ file: File; imported: PinterestImport }> {
    let source = pin;
    if (pin.kind === 'video' && !pin.videoUrl) source = await api.resolve(pin.id);

    const imported = await api.importMedia(
        source.kind === 'video' && source.videoUrl
            ? { videoUrl: source.videoUrl, pinId: source.id }
            : { imageUrl: source.imageUrl },
    );
    const res = await fetch(imported.url);
    if (!res.ok) throw new Error(`download ${res.status}`);
    const blob = await res.blob();
    const type = imported.contentType || blob.type;
    const ext = EXTENSIONS[type] || imported.url.split('?')[0].split('.').pop() || (imported.kind === 'video' ? 'mp4' : 'jpg');
    const file = new File([blob], `pinterest-${pin.id}.${ext}`, { type, lastModified: Date.now() });
    return { file, imported };
}
